import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { applyLanguage, getLanguageMode } from '../i18n';

/**
 * Topbar language toggle — 3-way cycle.
 *
 *   English (EN)  →  العربية (AR)  →  EN/AR (Bilingual)  →  English…
 *
 * Click flips to the next mode; `applyLanguage()` mutates <html dir/lang>,
 * persists to localStorage, and broadcasts an 'app-lang-changed' event so
 * the label below stays in sync with the actual mode (since the i18next
 * change listener doesn't fire when toggling between en ↔ both).
 *
 * For a full radio-style selector with explanatory text, see Settings page.
 */
const ORDER = ['en', 'ar', 'both'];
const LABEL = { en: 'EN', ar: 'AR', both: 'AR · EN' };

const LanguageToggle = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(() => getLanguageMode());

  useEffect(() => {
    const sync = () => setMode(getLanguageMode());
    window.addEventListener('app-lang-changed', sync);
    return () => window.removeEventListener('app-lang-changed', sync);
  }, []);

  const next = ORDER[(ORDER.indexOf(mode) + 1) % ORDER.length];

  const toggle = () => {
    applyLanguage(next);
    setMode(next);
  };

  return (
    <button
      data-testid="topbar-language-toggle"
      data-lang-mode={mode}
      onClick={toggle}
      title={t('topbar.language')}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700 transition"
    >
      <Languages className="w-4 h-4" />
      <span className="uppercase tracking-wider text-xs">
        {LABEL[mode]}
      </span>
    </button>
  );
};

export default LanguageToggle;
